// src/components/reports/VaccineEstimateDistributionReport.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // Correct import
import VaccineEstimateDistributionChart from "./VaccineEstimateDistributionChart";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// Load Sarabun font for PDF
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineEstimateDistributionReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    BudgetType: "",
    BudgetYear: "",
    EstimateUnit: "",
    VaccineID: "",
  });
  const [productionUnits, setProductionUnits] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // BudgetType options
  const budgetTypes = [
    { value: "Prevention", label: "งบป้องกัน" },
    { value: "Distribution", label: "งบจำหน่าย" },
  ];

  // Fetch production units
  const fetchProductionUnits = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeType",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setProductionUnits(response.OfficeList || []);
      } else {
        console.error("Failed to fetch production units:", response.error);
      }
    } catch (error) {
      console.error("Error fetching production units:", error);
    }
  };

  // Fetch vaccine options
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    {
      /*
    if (!filterData.BudgetType) {
      alert("กรุณาเลือกประเภทงบประมาณ");
      return;
    }
    */
    }
    if (!filterData.BudgetYear) {
      alert("กรุณากรอกปีงบประมาณ");
      return;
    }
    const budgetYearNum = parseInt(filterData.BudgetYear);
    if (isNaN(budgetYearNum) || budgetYearNum < 2567 || budgetYearNum > 2569) {
      alert("กรุณากรอกปีงบประมาณระหว่าง 2567 ถึง 2569");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineEstimateDistributionReport",
        data: {
          BudgetType: filterData.BudgetType,
          BudgetYear: filterData.BudgetYear,
          EstimateUnit: filterData.EstimateUnit || "",
          VaccineID: filterData.VaccineID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        // Normalize VaccneName to VaccineName
        const normalizedData = (response.VaccineEstimateReport || []).map(
          (item) => ({
            ...item,
            VaccineName: item.VaccneName || item.VaccineName || "ไม่ระบุ",
          })
        );
        setReportData(normalizedData);
      } else {
        setReportData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchProductionUnits();
      fetchVaccineOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  // Calculate total quantity
  const calculateTotal = (row) => {
    const quantities = [
      row.OctQty,
      row.NovQty,
      row.DecQty,
      row.JanQty,
      row.FebQty,
      row.MarQty,
      row.AprQty,
      row.MayQty,
      row.JunQty,
      row.JulQty,
      row.AugQty,
      row.SepQty,
    ].map(Number);
    return quantities.reduce((sum, qty) => sum + qty, 0);
  };

  // Generate month columns dynamically
  const getMonthColumns = (budgetYear) => {
    const year = budgetYear ? parseInt(budgetYear) : 2568;
    const prevYear = year - 1;
    const yearShort = year.toString().slice(-2);
    const prevYearShort = prevYear.toString().slice(-2);
    const months = [
      { key: "OctQty", label: `ต.ค. ${prevYearShort}` },
      { key: "NovQty", label: `พ.ย. ${prevYearShort}` },
      { key: "DecQty", label: `ธ.ค. ${prevYearShort}` },
      { key: "JanQty", label: `ม.ค. ${yearShort}` },
      { key: "FebQty", label: `ก.พ. ${yearShort}` },
      { key: "MarQty", label: `มี.ค. ${yearShort}` },
      { key: "AprQty", label: `เม.ย. ${yearShort}` },
      { key: "MayQty", label: `พ.ค. ${yearShort}` },
      { key: "JunQty", label: `มิ.ย. ${yearShort}` },
      { key: "JulQty", label: `ก.ค. ${yearShort}` },
      { key: "AugQty", label: `ส.ค. ${yearShort}` },
      { key: "SepQty", label: `ก.ย. ${yearShort}` },
    ];

    return months.map((month) => ({
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>{month.label}</span>
        </HStack>
      ),
      selector: (row) => Number(row[month.key]).toLocaleString("th-TH"),
      sortable: true,
      width: "120px",
    }));
  };

  // Table columns
  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>หน่วยงาน</span>
        </HStack>
      ),
      selector: (row) => row.EstimateUnit,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineName,
      sortable: true,
      width: "200px",
    },
    ...getMonthColumns(filterData.BudgetYear),
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รวมทั้งสิ้น</span>
        </HStack>
      ),
      selector: (row) => calculateTotal(row).toLocaleString("th-TH"),
      sortable: true,
      width: "150px",
    },
  ];

  // Export to Excel
  const exportToExcel = () => {
    const wsData = reportData.map((item) => {
      const data = {
        หน่วยงาน: item.EstimateUnit,
        รหัสวัคซีน: item.VaccineID,
        ชื่อวัคซีน: item.VaccineName,
      };
      const year = filterData.BudgetYear
        ? parseInt(filterData.BudgetYear)
        : 2568;
      const prevYear = year - 1;
      const yearShort = year.toString().slice(-2);
      const prevYearShort = prevYear.toString().slice(-2);
      data[`ต.ค. ${prevYearShort}`] = item.OctQty;
      data[`พ.ย. ${prevYearShort}`] = item.NovQty;
      data[`ธ.ค. ${prevYearShort}`] = item.DecQty;
      data[`ม.ค. ${yearShort}`] = item.JanQty;
      data[`ก.พ. ${yearShort}`] = item.FebQty;
      data[`มี.ค. ${yearShort}`] = item.MarQty;
      data[`เม.ย. ${yearShort}`] = item.AprQty;
      data[`พ.ค. ${yearShort}`] = item.MayQty;
      data[`มิ.ย. ${yearShort}`] = item.JunQty;
      data[`ก.ค. ${yearShort}`] = item.JulQty;
      data[`ส.ค. ${yearShort}`] = item.AugQty;
      data[`ก.ย. ${yearShort}`] = item.SepQty;
      data["รวมทั้งสิ้น"] = calculateTotal(item);
      return data;
    });
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EstimateDistributionReport");
    XLSX.writeFile(wb, "VaccineEstimateDistributionReport.xlsx");
  };

  // Export to PDF
  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun-Regular.ttf", fontBase64);
      doc.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(14);
    doc.text("รายงานปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย)", 12, 10);
    doc.text(`ประจำปีงบประมาณ: ${filterData.BudgetYear || "ไม่ระบุ"}`, 150, 10);
    doc.text(`หน่วยงาน: ${filterData.EstimateUnit || "ไม่ระบุ"}`, 220, 10);
    doc.setFontSize(10);

    doc.text(
      `ประเภทงบประมาณ: ${
        budgetTypes.find((t) => t.value === filterData.BudgetType)?.label ||
        "งบจำหน่าย"
      }`,
      14,
      18
    );
    const year = filterData.BudgetYear ? parseInt(filterData.BudgetYear) : 2568;
    const prevYear = year - 1;
    const yearShort = year.toString().slice(-2);
    const prevYearShort = prevYear.toString().slice(-2);

    autoTable(doc, {
      startY: 27,
      head: [
        [
          "หน่วยงาน",
          "รหัสวัคซีน",
          "ชื่อวัคซีน",
          `ต.ค. ${prevYearShort}`,
          `พ.ย. ${prevYearShort}`,
          `ธ.ค. ${prevYearShort}`,
          `ม.ค. ${yearShort}`,
          `ก.พ. ${yearShort}`,
          `มี.ค. ${yearShort}`,
          `เม.ย. ${yearShort}`,
          `พ.ค. ${yearShort}`,
          `มิ.ย. ${yearShort}`,
          `ก.ค. ${yearShort}`,
          `ส.ค. ${yearShort}`,
          `ก.ย. ${yearShort}`,
          "รวมทั้งสิ้น",
        ],
      ],
      body: reportData.map((item) => [
        item.EstimateUnit,
        item.VaccineID,
        item.VaccineName,
        Number(item.OctQty).toLocaleString("th-TH"),
        Number(item.NovQty).toLocaleString("th-TH"),
        Number(item.DecQty).toLocaleString("th-TH"),
        Number(item.JanQty).toLocaleString("th-TH"),
        Number(item.FebQty).toLocaleString("th-TH"),
        Number(item.MarQty).toLocaleString("th-TH"),
        Number(item.AprQty).toLocaleString("th-TH"),
        Number(item.MayQty).toLocaleString("th-TH"),
        Number(item.JunQty).toLocaleString("th-TH"),
        Number(item.JulQty).toLocaleString("th-TH"),
        Number(item.AugQty).toLocaleString("th-TH"),
        Number(item.SepQty).toLocaleString("th-TH"),
        calculateTotal(item).toLocaleString("th-TH"),
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: "white",
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 50 },
      },
    });

    doc.save("VaccineEstimateDistributionReport.pdf");
  };

  // Print table
  const printTable = () => {
    const year = filterData.BudgetYear ? parseInt(filterData.BudgetYear) : 2568;
    const prevYear = year - 1;
    const yearShort = year.toString().slice(-2);
    const prevYearShort = prevYear.toString().slice(-2);

    const printContent = `
      <html>
        <head>
          <title>Vaccine Estimate Distribution Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>รายงานปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย) ประจำปีงบประมาณ ${
            filterData.BudgetYear || "ไม่ระบุ"
          }</h2>
          <p>หน่วยงาน: ${filterData.EstimateUnit || "ไม่ระบุ"}</p>
          <p>ประเภทงบประมาณ: ${
            budgetTypes.find((t) => t.value === filterData.BudgetType)?.label ||
            "ไม่ระบุ"
          }</p>
          <table>
            <thead>
              <tr>
                <th>หน่วยงาน</th>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>ต.ค. ${prevYearShort}</th>
                <th>พ.ย. ${prevYearShort}</th>
                <th>ธ.ค. ${prevYearShort}</th>
                <th>ม.ค. ${yearShort}</th>
                <th>ก.พ. ${yearShort}</th>
                <th>มี.ค. ${yearShort}</th>
                <th>เม.ย. ${yearShort}</th>
                <th>พ.ค. ${yearShort}</th>
                <th>มิ.ย. ${yearShort}</th>
                <th>ก.ค. ${yearShort}</th>
                <th>ส.ค. ${yearShort}</th>
                <th>ก.ย. ${yearShort}</th>
                <th>รวมทั้งสิ้น</th>
              </tr>
            </thead>
            <tbody>
              ${reportData
                .map(
                  (item) => `
                  <tr>
                    <td>${item.EstimateUnit}</td>
                    <td>${item.VaccineID}</td>
                    <td>${item.VaccineName}</td>
                    <td>${Number(item.OctQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.NovQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.DecQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.JanQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.FebQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.MarQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.AprQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.MayQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.JunQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.JulQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.AugQty).toLocaleString("th-TH")}</td>
                    <td>${Number(item.SepQty).toLocaleString("th-TH")}</td>
                    <td>${calculateTotal(item).toLocaleString("th-TH")}</td>
                  </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย)" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย)"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            {/*
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทงบประมาณ
              </FormLabel>
              <Select
                name="BudgetType"
                value={filterData.BudgetType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทงบประมาณ"
                colorScheme="iBlue"
              >
                {budgetTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            */}
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ปีงบประมาณ (พ.ศ.)
              </FormLabel>
              <Input
                name="BudgetYear"
                type="number"
                value={filterData.BudgetYear}
                onChange={handleInputChange}
                placeholder="กรอกปีงบประมาณ (เช่น 2568)"
                colorScheme="iBlue"
                min={2567}
                max={2569}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                หน่วยงาน
              </FormLabel>
              <Select
                name="EstimateUnit"
                value={filterData.EstimateUnit}
                onChange={handleInputChange}
                placeholder="เลือกหน่วยงาน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {productionUnits.map((unit) => (
                  <option key={unit.OfficeID} value={unit.OfficeID}>
                    {unit.OfficeName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    BudgetType: "",
                    BudgetYear: "",
                    EstimateUnit: "",
                    VaccineID: "",
                  })
                }
              />
              <Search onClick={fetchReportData} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Table */}
      {reportData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายงานปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย)"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={reportData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
              noDataComponent="ไม่มีข้อมูลแสดงผล"
            />
          </Box>
        </Box>
      )}

      {/* Chart */}
      {reportData.length > 0 && vaccineOptions.length > 0 && (
        <VaccineEstimateDistributionChart
          data={reportData}
          vaccineOptions={vaccineOptions}
          budgetYear={filterData.BudgetYear}
        />
      )}
    </Box>
  );
};

export default VaccineEstimateDistributionReport;
